import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  ActionType,
  AnalyticsEventImportance,
  ComponentType,
  logStatement,
} from '@cbhq/client-analytics';
import {
  useGetProfileMenuV2,
  useGetProfileMenuV2Callback,
  useRefreshProfileMenuV2,
} from '@cbhq/identity-api-hooks/profile-menu/fetch';
import type { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';
import { getClientLocale } from '@cbhq/identity-shared/utils';

import { resourceContextStore } from '../../Organization/ResourceContextStore';
import { ErrorType } from '../../types/errors';
import { useAnalytics } from '../hooks/useAnalytics';
import { useRedirectToUL } from '../hooks/useRedirectToUL';
import { useGetUserIdFromSession } from '../hooks/useSession';
import { ProfileMenuContextType } from '../types';
import { parseHelpMenu } from '../utils/helpMenu';
import { parseActions } from '../utils/parseAction';
import { parseResources } from '../utils/parseResources';
import { handleProfileExpired, isExpired } from '../utils/profile';

import { useClientProps } from './ClientPropsProvider';

type ProfileMenuProviderProps = { children: ReactNode };

export const ProfileMenuV2Context = createContext<() => ProfileMenuContextType>(() => {
  throw new Error('ProfileMenuV2Context must be used within a ProfileMenuV2Provider');
});

export function ProfileMenuV2Provider({ children }: ProfileMenuProviderProps) {
  const {
    config,
    projectId,
    action,
    isUserSwitcher,
    onUnauthorized,
    hidePersonalResource,
    hidePersonalLegalEntity,
    onResourceListChange,
  } = useClientProps();
  const { logEvent } = useAnalytics();
  const getUserId = useGetUserIdFromSession();
  const redirectToUL = useRedirectToUL();

  const [parsedResources, setParsedResources] = useState<CoinbaseProfileMenuResource[]>([]);

  const isInitialized = useRef(false);
  const initializedAction = useRef<string | undefined>(undefined);
  // The parse config (hide flags + userUuid) last applied to parsedResources.
  // Used to bypass the same-action polling guard when the config changes — e.g.
  // an experiment resolving false→true after mount must re-filter the list.
  const lastParseConfig = useRef<string | undefined>(undefined);

  const [userSwitcherEnabled, setUserSwitcherEnabled] = useState(isUserSwitcher ?? false);

  const [isSigningOut, setIsSigningOut] = useState(false);

  const signingOut = useCallback(() => {
    setIsSigningOut(true);
  }, []);

  const loggedProfileMenuFetchError = useRef<string | undefined>(undefined);

  const userSwitcherFallback = useCallback(() => {
    if (!isSigningOut) {
      // do not fall back if user is signing out
      logEvent('user_switcher_fallback', {
        action: ActionType.process,
        componentType: ComponentType.dropdown,
        actionType: 'user_switcher_fallback',
      });

      setUserSwitcherEnabled(false);
    }
  }, [isSigningOut, logEvent]);

  const profileMenuPayload = useMemo(
    () => ({
      clientOptions: {
        projectId,
        action,
        locale: config?.locale ?? getClientLocale(),
      },
      polling: !isInitialized.current,
    }),
    [config?.locale, projectId, action],
  );

  const refreshProfile = useRefreshProfileMenuV2(profileMenuPayload);

  const handleProfileMenuFetchError = useCallback((profileMenuError: Error) => {
    if (loggedProfileMenuFetchError.current === profileMenuError.message) {
      return;
    }

    loggedProfileMenuFetchError.current = profileMenuError.message;

    logStatement('Profile menu fetch error', {
      level: 'error',
      metadata: { error: profileMenuError.message },
    });
  }, []);

  const profileMenuResult = useGetProfileMenuV2(profileMenuPayload, {
    returnStaleDataOnError: true,
    onError: handleProfileMenuFetchError,
  });

  const { actions, helpMenu, labels, resources, profiles, profileManagementUrl, error } =
    profileMenuResult ?? {};

  const getProfileCallback = useGetProfileMenuV2Callback();

  useEffect(() => {
    if (!error) {
      return;
    }

    logStatement('Profile menu response error', {
      level: 'error',
      metadata: { error },
    });

    if (error.split(':')[0] === ErrorType.Unauthorized) {
      userSwitcherFallback();
      // Calling onUnauthorized provided by the client to handle the unauthorized error
      onUnauthorized?.();
    }
  }, [error, onUnauthorized, userSwitcherFallback]);

  const userUuid = resources?.find((r) => r.resourceType === 'user')?.identityAccountId;

  useEffect(() => {
    const parsed = parseResources(
      resources,
      hidePersonalResource,
      hidePersonalLegalEntity,
      userUuid,
    );

    const parseConfig = `${String(hidePersonalResource)}|${String(hidePersonalLegalEntity)}|${
      userUuid ?? ''
    }`;
    const parseConfigChanged = lastParseConfig.current !== parseConfig;

    // Avoid removing resources on polling for same action when both old and new resources exist.
    // Allow update when new resources are empty so RCID is cleared for paths without resources.
    // Also always re-parse when the parse config (hide flags / userUuid) changed — e.g. an
    // experiment gating hidePersonalResource/hidePersonalLegalEntity resolving after mount —
    // otherwise the guard would leave a now-excluded resource in the already-parsed list.
    if (
      !parseConfigChanged &&
      initializedAction.current === action &&
      parsedResources.length > 0 &&
      parsed.length > 0
    ) {
      return;
    }
    initializedAction.current = action;
    lastParseConfig.current = parseConfig;

    setParsedResources(parsed);

    resourceContextStore.setResources(parsed);
  }, [
    resources,
    hidePersonalResource,
    hidePersonalLegalEntity,
    userUuid,
    action,
    parsedResources.length,
  ]);

  useEffect(() => {
    onResourceListChange?.(resources);
  }, [resources, onResourceListChange]);

  if (profileMenuResult && !error && (!profiles || profiles.length === 0)) {
    logStatement('Empty profiles in profile menu', {
      level: 'error',
    });
  }

  const [selectedProfile, otherProfiles] = useMemo(() => {
    return [profiles?.[0], profiles?.slice(1)];
  }, [profiles]);

  useEffect(() => {
    if (selectedProfile?.expiresAt && isExpired(selectedProfile.expiresAt)) {
      // desc: handle primary profile expired which redirect user to login page with email prefill.
      logEvent('user_switcher_selected_profile', {
        action: ActionType.view,
        componentType: ComponentType.dropdown,
        actionType: 'selected_profile_is_expired',
      });

      handleProfileExpired(selectedProfile.email, config?.loginUrl);
    }
  }, [config?.loginUrl, logEvent, selectedProfile]);

  useEffect(() => {
    const initializeUser = async () => {
      if (!isInitialized.current) {
        isInitialized.current = true;
        initializedAction.current = action;

        const userId = await getUserId();

        if (userId && selectedProfile?.userUuid && selectedProfile.userUuid !== userId) {
          // desc: checking if the usm user and login-session user is the same.
          // if fetch session failed then don't block the flow, just log the error and continue
          logEvent(
            'user_switcher_redirect_to_ul',
            {
              action: ActionType.view,
              componentType: ComponentType.dropdown,
              selectedProfileUserId: selectedProfile?.userUuid,
              currentUserId: userId,
            },
            AnalyticsEventImportance.high,
          );
          redirectToUL();
        }
      }
    };

    void initializeUser();
  }, [action, getUserId, redirectToUL, selectedProfile, logEvent]);

  const getSyncProfileCallback = useCallback(async () => {
    const response = await getProfileCallback(profileMenuPayload);

    if (response instanceof Error) {
      logEvent('user_switcher_get_profile_callback_error', {
        action: ActionType.api,
        componentType: ComponentType.dropdown,
        error: response.message,
      });

      return profiles;
    }

    return response.profiles ?? [];
  }, [getProfileCallback, logEvent, profileMenuPayload, profiles]);

  const contextValue = useCallback(
    (): ProfileMenuContextType => ({
      selectedProfile,
      actions: actions?.map(parseActions) ?? [],
      labels: labels ?? [],
      resources: parsedResources,
      profileManagementUrl,
      isSigningOut,
      signingOut,
      userSwitcherFallback,
      userSwitcherEnabled,
      otherProfiles: otherProfiles ?? [],
      helpMenu: helpMenu && parseHelpMenu(helpMenu),
      refreshProfile,
      getSyncProfileCallback,
    }),
    [
      selectedProfile,
      actions,
      labels,
      parsedResources,
      profileManagementUrl,
      isSigningOut,
      signingOut,
      userSwitcherFallback,
      userSwitcherEnabled,
      otherProfiles,
      helpMenu,
      refreshProfile,
      getSyncProfileCallback,
    ],
  );

  return (
    <ProfileMenuV2Context.Provider value={contextValue}>{children}</ProfileMenuV2Context.Provider>
  );
}

export function useProfileMenuV2() {
  return useContext(ProfileMenuV2Context)();
}

export function useProfileInfo() {
  const { selectedProfile } = useProfileMenuV2();

  return {
    name: selectedProfile?.name ?? '',
    email: selectedProfile?.email ?? '',
    avatarUrl: selectedProfile?.avatarUrl ?? '',
    managedByOrganizationName: selectedProfile?.managedByOrganizationName ?? '',
  };
}
